import type { NodeV2 } from '@vedalib/editor'
import type { EditorElement } from '@vedalib/editor/lib/elements'
import * as R from 'ramda'
import React, { useCallback, useMemo, useState } from 'react'

import type { IComplexNode } from 'components/Editor/NodeV2/Picker/complexNodes'
import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import Empty from 'components/uiKit/Empty'
import Search from 'components/uiKit/Search'
import { EMPTY_OBJECT } from 'constants/commonConstans'
import { useProjectDispatch } from 'services/Store/Project/hooks'
import type { Block, MapType } from 'services/Store/Project/types'
import { setUserPreferencesItems } from 'services/Store/Users/reducer'
import { selectUserPreferences } from 'services/Store/Users/selectors'
import { useAppSelector } from 'services/Store/hooks'

import TileNode from '../TileNode'
import * as s from './TabAsset.module.scss'

interface ITabAssetProps {
  block: Block
  parent: NodeV2
  onAdd: (
    node: NodeV2,
    data?: { nodes?: MapType<NodeV2>; elements?: MapType<EditorElement> },
  ) => void
}

const TabAsset: React.FC<ITabAssetProps> = ({ block, parent, onAdd }) => {
  const dispatch = useProjectDispatch()
  const preferencesNodes = useAppSelector(selectUserPreferences).nodesV2 || EMPTY_OBJECT
  const [search, setSearch] = useState<string>()

  const userItems: IComplexNode[] = useMemo(
    () =>
      Object.values(preferencesNodes)
        .map((item) => JSON.parse(String(item)))
        .filter(({ root }) => !search || root.label?.toLowerCase().includes(search.toLowerCase())),
    [preferencesNodes, search],
  )

  const handleDeleteNode = useCallback(
    (item: NodeV2) => {
      const nodesV2 = R.omit([item.id], preferencesNodes)
      dispatch(setUserPreferencesItems({ nodesV2 }))
    },
    [dispatch, preferencesNodes],
  )

  return (
    <div className={s.root}>
      <Padding bottom sides top>
        <Search
          disabled={!userItems.length}
          name='elements'
          onChange={setSearch}
          value={search}
          width='100%'
        />
      </Padding>
      <LayoutScroll sizeAutoCapable>
        <Padding bottom gap sides>
          <div className={s.items}>
            {userItems.length > 0 && (
              <div className={s.items}>
                {userItems.map(({ root, elements, nodes }) => (
                  <TileNode
                    block={block}
                    elements={elements}
                    item={root}
                    key={root.id}
                    nodes={nodes}
                    onAdd={onAdd}
                    onDelete={handleDeleteNode}
                    parent={parent}
                  />
                ))}
              </div>
            )}
          </div>
          {userItems.length === 0 && <Empty />}
        </Padding>
      </LayoutScroll>
    </div>
  )
}

export default TabAsset
